import { useEffect, useRef } from 'react'

import { Badge } from './Badge'

import styles from './GtHeader.module.scss'

import type { GtStatus, PrefillOrigin } from '@/api/internal'
import Button from '@/components/Button'
import type { GtProgress } from '@/pages/app/AttributionViewer/annotate/gtDerivations'
import { PREFILL_ORIGIN_LABEL } from '@/pages/app/AttributionViewer/annotate/gtModel'
import type { GtSaveStatus } from '@/pages/app/AttributionViewer/annotate/useGtDocument'
import type { UseGtFinalizeResult } from '@/pages/app/AttributionViewer/annotate/useGtFinalize'

const STATUS_LABEL: Record<GtStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  finalized: 'Finalized',
  finalized_with_edits: 'Finalized (edited)',
}

export const SAVE_STATE_COPY: Record<GtSaveStatus, string> = {
  saving: 'Saving…',
  saved: 'Saved',
  unsaved: 'Unsaved changes',
  error: "Couldn't save — retry",
  conflict: 'Someone else saved a newer version',
  readonly: 'Read-only',
}

const SAVE_STATE_TONE: Record<GtSaveStatus, string> = {
  saving: 'saving',
  saved: 'saved',
  unsaved: 'unsaved',
  error: 'error',
  conflict: 'conflict',
  readonly: 'neutral',
}

export interface GtHeaderFinalizeProps extends UseGtFinalizeResult {
  onReload: () => void
  onFocusTurn: (turnId: string) => void
}

export interface GtHeaderDiscardProps {
  busy: boolean
  disabled: boolean
  error: string | null
  onRequest: () => void
  onDismissError: () => void
}

export interface GtHeaderProps {
  status: GtStatus
  version: number
  prefillOrigin: PrefillOrigin
  progress: GtProgress
  saveStatus: GtSaveStatus
  errorReason?: string | null
  onRetry?: () => void
  discard?: GtHeaderDiscardProps
  finalize?: GtHeaderFinalizeProps
}

const DiscardControls = ({ discard }: { discard: GtHeaderDiscardProps }) => (
  <div className={styles.discardGroup}>
    {discard.error && (
      <span className={styles.finalizeError} role="alert">
        {discard.error}
        <button type="button" className={styles.retryButton} onClick={discard.onDismissError}>
          Dismiss
        </button>
      </span>
    )}
    <Button
      title="Start over"
      type="light"
      onClick={discard.onRequest}
      isDisabled={discard.disabled}
      isLoading={discard.busy}
      className={styles.discardButton}
    />
  </div>
)

const FinalizeControls = ({ finalize }: { finalize: GtHeaderFinalizeProps }) => {
  const buttonHostRef = useRef<HTMLSpanElement>(null)
  const wasConfirmingRef = useRef(false)

  // Focus returns to the trigger once the confirm dialog closes, whichever way it closed.
  useEffect(() => {
    if (wasConfirmingRef.current && !finalize.confirmOpen) {
      buttonHostRef.current?.querySelector('button')?.focus()
    }
    wasConfirmingRef.current = finalize.confirmOpen
  }, [finalize.confirmOpen])

  const disabledTurnId = finalize.disabledTurnId
  const button = (
    <span ref={buttonHostRef} className={styles.finalizeButtonHost}>
      <Button
        title="Finalize"
        type="dark"
        onClick={finalize.request}
        isDisabled={!finalize.canFinalize}
        isLoading={finalize.busy}
        className={styles.finalizeButton}
      />
    </span>
  )

  return (
    <div className={styles.finalizeGroup}>
      <span className={styles.finalizeState} data-tone={finalize.tone} role="status" aria-live="polite">
        <span className={styles.saveDot} />
        {finalize.label}
      </span>
      {finalize.error && (
        <span className={styles.finalizeError} role="alert">
          {finalize.error.message}
          {finalize.error.needsReload && (
            <button type="button" className={styles.retryButton} onClick={finalize.onReload}>
              Reload
            </button>
          )}
          {finalize.error.turnId && (
            <button
              type="button"
              className={styles.retryButton}
              onClick={() => {
                const turnId = finalize.error?.turnId
                if (turnId) finalize.onFocusTurn(turnId)
                finalize.dismissError()
              }}
            >
              Go to turn
            </button>
          )}
          {!finalize.error.needsReload && !finalize.error.turnId && (
            <button type="button" className={styles.retryButton} onClick={finalize.dismissError}>
              Dismiss
            </button>
          )}
        </span>
      )}
      {finalize.disabledReason && !finalize.busy && !finalize.error && (
        <span className={styles.finalizeBlocked} role="status">
          {finalize.disabledReason}
          {disabledTurnId && (
            <button type="button" className={styles.retryButton} onClick={() => finalize.onFocusTurn(disabledTurnId)}>
              Go to turn
            </button>
          )}
        </span>
      )}
      {button}
    </div>
  )
}

export const GtHeader = ({
  status,
  version,
  prefillOrigin,
  progress,
  saveStatus,
  errorReason,
  onRetry,
  discard,
  finalize,
}: GtHeaderProps) => {
  const pct = progress.turnCount > 0 ? Math.round((progress.reviewedCount / progress.turnCount) * 100) : 0
  return (
    <header className={styles.header}>
      <div className={styles.titleGroup}>
        <span className={styles.title}>Ground truth review</span>
        <span className={styles.statusChip} data-status={status}>
          {STATUS_LABEL[status]}
        </span>
        <span className={styles.meta}>v{version}</span>
        <Badge>Seed: {PREFILL_ORIGIN_LABEL[prefillOrigin]}</Badge>
      </div>
      <div className={styles.progressGroup}>
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: `${pct}%` }} />
        </div>
        <span className={styles.progressLabel}>
          {progress.reviewedCount}/{progress.turnCount} reviewed
        </span>
        <span className={styles.saveState} data-tone={SAVE_STATE_TONE[saveStatus]} role="status" aria-live="polite">
          <span className={styles.saveDot} />
          {SAVE_STATE_COPY[saveStatus]}
          {saveStatus === 'error' && errorReason && <span className={styles.saveReason}>: {errorReason}</span>}
        </span>
        {saveStatus === 'error' && onRetry && (
          <button type="button" className={styles.retryButton} onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
      {discard && <DiscardControls discard={discard} />}
      {finalize && <FinalizeControls finalize={finalize} />}
    </header>
  )
}
